import React, { useState, useRef, useEffect } from 'react';

export default function ImageCropperModal({ imageUrl, aspectRatio, onClose, onCrop }) {
    const imageRef = useRef(null);
    const cropperRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(imageUrl);
    const [currentRatio, setCurrentRatio] = useState(Number.isNaN(aspectRatio) ? null : aspectRatio);

    const initCropper = () => {
        if (cropperRef.current) cropperRef.current.destroy();
        if (imageRef.current && window.Cropper) {
            cropperRef.current = new window.Cropper(imageRef.current, {
                aspectRatio: currentRatio !== null ? currentRatio : NaN,
                viewMode: 1,
                dragMode: 'move',
                background: false,
                autoCropArea: 0.8,
                checkCrossOrigin: false, // Prevents CORS reload loop
                cropend: () => {
                    if (cropperRef.current) {
                        try {
                            const canvas = cropperRef.current.getCroppedCanvas({ width: 400 });
                            if (canvas) setPreviewUrl(canvas.toDataURL('image/jpeg', 0.6));
                        } catch(e) { console.warn("CORS issue on preview", e); }
                    }
                },
                ready: () => {
                    if (cropperRef.current) {
                        try {
                            const canvas = cropperRef.current.getCroppedCanvas({ width: 400 });
                            if (canvas) setPreviewUrl(canvas.toDataURL('image/jpeg', 0.6));
                        } catch(e) { console.warn("CORS issue on preview", e); }
                    }
                }
            });
        }
    };

    useEffect(() => {
        // Catch already loaded images (e.g., from cache)
        if (imageRef.current && imageRef.current.complete) {
            initCropper();
        }
        return () => {
            if (cropperRef.current) cropperRef.current.destroy();
        };
    }, []);

    const handleRatioChange = (ratio) => {
        setCurrentRatio(ratio);
        if (cropperRef.current) {
            cropperRef.current.setAspectRatio(ratio || NaN);
        }
    };

    const handleCrop = () => {
        if (cropperRef.current) {
            try {
                const canvas = cropperRef.current.getCroppedCanvas();
                if (!canvas) {
                    alert("Could not generate crop. Please try again.");
                    return;
                }
                canvas.toBlob((blob) => {
                    onCrop(blob);
                }, 'image/jpeg');
            } catch(e) {
                console.error("Crop error", e);
                alert("Security error: The original image prevents direct cropping. Please try uploading the file again.");
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 overflow-y-auto">
            <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-5xl w-full flex flex-col md:flex-row gap-6 my-auto">
                <div className="w-full md:w-2/3 flex flex-col">
                    <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-gray-900">Crop Image</h3>
                        {(!aspectRatio || Number.isNaN(aspectRatio)) && (
                            <div className="flex gap-2">
                                <button onClick={() => handleRatioChange(16/9)} className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${currentRatio === 16/9 ? 'bg-pink-500 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Landscape (16:9)</button>
                                <button onClick={() => handleRatioChange(3/4)} className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${currentRatio === 3/4 ? 'bg-pink-500 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Portrait (3:4)</button>
                                <button onClick={() => handleRatioChange(null)} className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${!currentRatio ? 'bg-pink-500 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Freeform</button>
                            </div>
                        )}
                    </div>
                    <div className="w-full h-64 md:h-96 bg-gray-900 rounded-xl overflow-hidden shadow-inner flex items-center justify-center">
                        <img 
                            ref={imageRef} 
                            src={imageUrl} 
                            alt="Crop preview" 
                            style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }} 
                            crossOrigin={imageUrl.startsWith('http') ? "anonymous" : undefined}
                            onLoad={initCropper}
                        />
                    </div>
                </div>
                
                <div className="w-full md:w-1/3 flex flex-col border-t md:border-t-0 md:border-l border-gray-200 pt-6 md:pt-0 md:pl-6">
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Live Previews</h3>
                    <p className="text-xs text-gray-500 mb-6">See how your image will automatically adapt to different screen sizes and modals (like Save the Dates).</p>
                    
                    <div className="space-y-6 flex-grow">
                        <div>
                            <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> 
                                Desktop (Portrait Crop)
                            </p>
                            <div className="w-32 h-48 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-inner mx-auto relative ring-4 ring-gray-50">
                                <img src={previewUrl} className="absolute inset-0 w-full h-full object-cover transition-all duration-300" alt="Desktop Preview" />
                            </div>
                        </div>
                        
                        <div>
                            <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg> 
                                Mobile (Landscape Crop)
                            </p>
                            <div className="w-full h-32 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-inner relative ring-4 ring-gray-50">
                                <img src={previewUrl} className="absolute inset-0 w-full h-full object-cover transition-all duration-300" alt="Mobile Preview" />
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-gray-100">
                        <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold transition">Cancel</button>
                        <button onClick={handleCrop} className="px-5 py-2.5 rounded-xl bg-pink-600 hover:bg-pink-700 text-white font-bold transition shadow-md">Crop & Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}